import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@grebban/style-system-react';
import { useTranslation } from 'react-i18next';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import { above, media } from '@fogia/core-web/utils/mediaqueries';
import useEmblaCarousel from 'embla-carousel-react';
import { get } from '@fogia/core-web/libs/GrebbCommerceAPI/util/authClient';
import Button from 'components/Button';
import SearchInputField from 'components/SearchInputField';
import Text from 'components/Text';
import ChevronLeft from 'assets/media/icons/ChevronLeft';
import PortableText from 'libs/sanity/components/PortableText';
import StoreFinderMap from './StoreFinderMap';
import { type Store } from './storeData';

/* --------------- Typography constants --------------- */

const TITLE_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/18_120_510_-3',
    null,
    null,
    null,
    null,
    'Primary/20_120_510_-3',
];

const STORE_NAME_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];

const STORE_ADDRESS_TYPOGRAPHY: (string | null)[] = [
    'Secondary/Mobile/13_120_400',
    null,
    null,
    null,
    null,
    'Secondary/15_120_400',
];

const CONTACT_LINK_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];

const DETAIL_HEADING_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/18_120_510_-3',
    null,
    null,
    null,
    null,
    'Primary/20_120_510_-3',
];

const DETAIL_LABEL_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];

const DETAIL_VALUE_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];

const DETAIL_SECONDARY_TYPOGRAPHY: (string | null)[] = [
    'Secondary/Mobile/13_120_400',
    null,
    null,
    null,
    null,
    'Secondary/15_120_400',
];

const SEARCH_INPUT_TYPOGRAPHY: (string | null)[] = [
    'Primary/24_110_510_-3',
    null,
    null,
    null,
    null,
    'Primary/30_100_510_-5',
];

/* --------------- Overlay wrapper --------------- */

const OverlayWrapper = styled('div')<{ $isOpen: boolean }>`
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    z-index: 101;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    transition:
        opacity var(--transition-primary-fast),
        visibility var(--transition-primary-fast);
    display: flex;
    flex-direction: column;
    background-color: var(--ui-background);
`;

/* --------------- Panel header (orange bar) --------------- */

const PanelHeader = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    background-color: var(--ui-background);
    flex-shrink: 0;
`;

/* --------------- Content layout --------------- */

const ContentLayout = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    ${above['desktop.sm']} {
        flex-direction: row;
        overflow: hidden;
    }
`;

const MapContainer = styled('div')`
    width: 100%;
    aspect-ratio: 1 / 1;
    flex-shrink: 0;

    ${above['desktop.sm']} {
        width: 58%;
        aspect-ratio: unset;
        height: 100%;
    }
`;

const StorePanel = styled('div')`
    overflow-x: clip;
    position: relative;

    ${above['desktop.sm']} {
        flex: 1;
        width: var(--overlay-width-desktop);
        overflow: hidden;
    }
`;

const SlidingTrack = styled('div')<{ $showDetail: boolean }>`
    display: flex;
    width: 200%;
    transform: translateX(${({ $showDetail }) => ($showDetail ? '-50%' : '0')});
    transition: var(--transition-primary-fast);

    ${above['desktop.sm']} {
        height: 100%;
    }
`;

const SlideView = styled('div')`
    width: 50%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    padding: 10px;

    ${above['desktop.sm']} {
        overflow-y: auto;
        height: 100%;
    }
`;

/* --------------- Search field --------------- */

const SearchFieldWrapper = styled('div')`
    padding-top: 20px;
`;

const NoResultsMessage = styled('div')`
    padding-top: 10px;
`;

/* --------------- Filter accordion --------------- */

const TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
    shop: { key: 'store_finder.type.shop', fallback: 'Shops & Retailers' },
    contractDealer: { key: 'store_finder.type.contract_dealer', fallback: 'Contract Dealers' },
    showroom: { key: 'store_finder.type.showroom', fallback: 'Fogia Showrooms' },
    agentDistributor: { key: 'store_finder.type.agent_distributor', fallback: 'Agent / Distributor' },
};

const FilterSection = styled('div')`
    padding-top: 120px;
`;

const AccordionButton = styled('button')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 45px;
    padding: 0;
    background: none;
    border: none;
    border-top: 0.5px solid var(--stroke-default);
    cursor: pointer;
    color: var(--ui-primary-aa);
`;

const AccordionIcon = styled('span')<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    position: relative;

    &::before,
    &::after {
        content: '';
        position: absolute;
        background-color: currentColor;
    }

    &::before {
        width: 10px;
        height: 1px;
    }

    &::after {
        width: 1px;
        height: 10px;
        display: ${({ $isOpen }) => ($isOpen ? 'none' : 'block')};
    }
`;

const FilterList = styled('div')`
    display: flex;
    flex-direction: column;
`;

const FilterOption = styled('button')`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ui-primary-aa);
`;

const FilterCheckbox = styled('span')<{ $isActive: boolean }>`
    width: 7px;
    height: 7px;
    flex-shrink: 0;
    background-color: ${({ $isActive }) => ($isActive ? 'var(--ui-secondary)' : 'var(--opacity-primary-15)')};
`;

/* --------------- Store row (list view) --------------- */

const StoreRowWrapper = styled('div')`
    padding-top: 40px;
`;

const StoreRow = styled('div')`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 10px 0 13px;
    border-top: 0.5px solid var(--stroke-default);
`;

const StoreInfo = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ContactLinkWrapper = styled('div')`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

/* --------------- Detail view --------------- */

const BackButtonLabel = styled('span')`
    display: flex;
    align-items: center;
    gap: 10px;
`;
/* --------------- Image carousel --------------- */

const CarouselWrapper = styled('div')`
    margin: 10px -10px 0;
`;

const CarouselViewport = styled('div')`
    overflow: hidden;
`;

const CarouselContainer = styled('div')`
    display: flex;
`;

const CarouselSlide = styled('div')`
    flex: 0 0 100%;
    min-width: 0;
    aspect-ratio: 16 / 9;
`;

const CarouselImage = styled('img')`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const DotIndicators = styled('div')`
    display: flex;
    justify-content: center;
    gap: 5px;
    padding: 10px 0;
`;

const Dot = styled('button')<{ $isActive: boolean }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: none;
    padding: 0;
    cursor: pointer;
    background-color: ${({ $isActive }) => ($isActive ? 'var(--ui-secondary)' : 'rgba(23,23,23,0.15)')};
    transition: background-color 0.2s ease;
`;

/* --------------- Detail content --------------- */

const DetailContent = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 40px;
`;

const ContactSection = styled('div')`
    display: flex;
    gap: 10px;
    padding-top: 10px;
    border-top: 0.5px solid var(--stroke-default);
`;

const SectionLabel = styled('div')`
    flex: 1;
    min-width: 0;
`;

const SectionContent = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
`;

const LinkList = styled('div')`
    display: flex;
    flex-direction: column;
`;

const OpeningHoursItem = styled('div')`
    display: flex;
    flex-direction: column;
`;

/* --------------- Component --------------- */

export interface StoreFinderOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

const SRCSET_WIDTHS = [400, 600, 800, 1200];

const buildSrcSet = (url: string): string =>
    SRCSET_WIDTHS.map((width) => `${url}?w=${width}&auto=format ${width}w`).join(', ');

const StoreImageCarousel = ({ images }: { images: string[] }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on('select', onSelect);
        onSelect();
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    const handleDotClick = useCallback(
        (index: number) => {
            emblaApi?.scrollTo(index);
        },
        [emblaApi],
    );

    return (
        <CarouselWrapper>
            <CarouselViewport ref={emblaRef}>
                <CarouselContainer>
                    {images.map((imageUrl, index) => (
                        <CarouselSlide key={index}>
                            <CarouselImage
                                src={`${imageUrl}?w=800&auto=format`}
                                srcSet={buildSrcSet(imageUrl)}
                                sizes="(min-width: 1024px) 42vw, 100vw"
                                alt=""
                                loading="lazy"
                            />
                        </CarouselSlide>
                    ))}
                </CarouselContainer>
            </CarouselViewport>
            {images.length > 1 && (
                <DotIndicators>
                    {images.map((_, index) => (
                        <Dot
                            key={index}
                            $isActive={index === selectedIndex}
                            type="button"
                            onClick={() => handleDotClick(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </DotIndicators>
            )}
        </CarouselWrapper>
    );
};

const sortStoresForDisplay = (stores: Store[]): Store[] =>
    [...stores].sort((storeA, storeB) => {
        const aIsFogia = (storeA.name ?? '').trim().toLowerCase().startsWith('fogia');
        const bIsFogia = (storeB.name ?? '').trim().toLowerCase().startsWith('fogia');

        if (aIsFogia !== bIsFogia) return aIsFogia ? -1 : 1;

        return (storeA.name ?? '').localeCompare(storeB.name ?? '', 'sv', { sensitivity: 'base' });
    });

export const StoreFinderOverlay = ({ isOpen, onClose }: StoreFinderOverlayProps) => {
    const { t } = useTranslation();
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [displayedStore, setDisplayedStore] = useState<Store | null>(null);
    const [stores, setStores] = useState<Store[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
    const [isFilterOpen, setIsFilterOpen] = useState(true);
    const slidingTrackRef = useRef<HTMLDivElement>(null);

    const handleSelectStore = useCallback((store: Store) => {
        setDisplayedStore(store);
        setSelectedStore(store);
    }, []);

    const handleBackToList = useCallback(() => {
        setSelectedStore(null);
    }, []);

    // Clear displayed store after slide-out transition completes
    useEffect(() => {
        if (selectedStore) return;
        if (!displayedStore) return;

        const trackEl = slidingTrackRef.current;
        if (!trackEl) {
            setDisplayedStore(null);
            return;
        }

        const handleTransitionEnd = (event: TransitionEvent) => {
            if (event.propertyName === 'transform') {
                setDisplayedStore(null);
            }
        };

        trackEl.addEventListener('transitionend', handleTransitionEnd);
        return () => trackEl.removeEventListener('transitionend', handleTransitionEnd);
    }, [selectedStore, displayedStore]);

    // Fetch stores when overlay opens
    useEffect(() => {
        if (!isOpen) return;

        const abortController = new AbortController();
        const fetchStores = async () => {
            setIsLoading(true);
            try {
                const response = await get('/frontend/content/stores', {}, {}, abortController.signal);
                const data = await response.body();
                if (data && Array.isArray(data)) {
                    setStores(sortStoresForDisplay(data));
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    console.error('Failed to fetch stores:', error);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        fetchStores();
        return () => abortController.abort();
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Reset state when overlay closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedStore(null);
            setDisplayedStore(null);
            setSearchQuery('');
            setSelectedTypes(new Set());
            setIsFilterOpen(true);
        }
    }, [isOpen]);

    const availableTypes = useMemo(() => {
        const typeKeys = new Set<string>();
        stores.forEach((store) => {
            if (store.type) {
                Object.keys(store.type).forEach((key) => {
                    if (store.type?.[key as keyof typeof store.type]) {
                        typeKeys.add(key);
                    }
                });
            }
        });
        return Array.from(typeKeys);
    }, [stores]);

    const handleToggleType = useCallback((typeKey: string) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(typeKey)) {
                next.delete(typeKey);
            } else {
                next.add(typeKey);
            }
            return next;
        });
    }, []);

    const filteredStores = useMemo(
        () =>
            stores.filter((store) => {
                if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    const matchesSearch =
                        store.name?.toLowerCase().includes(query) ||
                        store.address.city?.toLowerCase().includes(query) ||
                        store.address.country?.toLowerCase().includes(query) ||
                        store.address.streetAddress?.toLowerCase().includes(query);
                    if (!matchesSearch) {
                        return false;
                    }
                }
                if (selectedTypes.size > 0 && store.type) {
                    const storeTypeKeys = Object.keys(store.type).filter(
                        (key) => store.type?.[key as keyof typeof store.type],
                    );
                    if (!storeTypeKeys.some((key) => selectedTypes.has(key))) {
                        return false;
                    }
                }
                return true;
            }),
        [stores, searchQuery, selectedTypes],
    );

    const renderStoreList = () => (
        <>
            <SearchFieldWrapper>
                <SearchInputField
                    type="text"
                    placeholder={t('store_finder.search_placeholder', 'Find the store closest to you')}
                    aria-label={t('store_finder.search_placeholder', 'Find the store closest to you')}
                    value={searchQuery}
                    onChange={setSearchQuery}
                    typography={SEARCH_INPUT_TYPOGRAPHY}
                />
            </SearchFieldWrapper>
            {isLoading && (
                <NoResultsMessage>
                    <Text $as="p" typography={STORE_NAME_TYPOGRAPHY} color="var(--ui-primary-aa)">
                        {t('store_finder.loading', 'Loading stores')}
                    </Text>
                </NoResultsMessage>
            )}
            {!isLoading && searchQuery && filteredStores.length === 0 && (
                <NoResultsMessage>
                    <Text $as="p" typography={STORE_NAME_TYPOGRAPHY} color="var(--ui-primary-aa)">
                        {t(
                            'store_finder.no_results',
                            "There doesn't seem to be any stores in this area.\nPlease try widening your search",
                        )}
                    </Text>
                </NoResultsMessage>
            )}
            {availableTypes.length > 0 && (
                <FilterSection>
                    <AccordionButton type="button" onClick={() => setIsFilterOpen((prev) => !prev)}>
                        <Text $as="span" typography={STORE_NAME_TYPOGRAPHY} color="var(--ui-primary-aa)">
                            {t('store_finder.active_filters', 'Active filters')}
                            {selectedTypes.size > 0 && ` (${selectedTypes.size})`}
                        </Text>
                        <AccordionIcon $isOpen={isFilterOpen} />
                    </AccordionButton>
                    {isFilterOpen && (
                        <FilterList>
                            {availableTypes.map((typeKey) => (
                                <FilterOption key={typeKey} type="button" onClick={() => handleToggleType(typeKey)}>
                                    <FilterCheckbox $isActive={selectedTypes.has(typeKey)} />
                                    <Text $as="span" typography={STORE_NAME_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                        {t(`store_finder.type.${typeKey}`, typeKey)}
                                    </Text>
                                </FilterOption>
                            ))}
                        </FilterList>
                    )}
                </FilterSection>
            )}
            {filteredStores.map((store) => (
                <StoreRowWrapper key={store._id}>
                    <StoreRow>
                        <StoreInfo>
                            <Text $as="span" typography={STORE_NAME_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                {store.name}
                            </Text>
                            <Text $as="span" typography={STORE_ADDRESS_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                {store.address.streetAddress} {store.address.zipCode}
                            </Text>
                            <Text $as="span" typography={STORE_ADDRESS_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                {store.address.city
                                    ? `${store.address.city}, ${store.address.country}`
                                    : store.address.country}
                            </Text>
                        </StoreInfo>
                        <ContactLinkWrapper>
                            <Button onClick={() => handleSelectStore(store)}>
                                {t('store_finder.contact_information', 'Contact information')}
                            </Button>
                        </ContactLinkWrapper>
                    </StoreRow>
                </StoreRowWrapper>
            ))}
        </>
    );

    const renderStoreDetail = (store: Store) => (
        <>
            <span>
                <Button theme="textButton" onClick={handleBackToList} typography={DETAIL_LABEL_TYPOGRAPHY}>
                    <BackButtonLabel>
                        <ChevronLeft size="xsmall" />
                        <span>{t('store_finder.back_to_store_finder', 'Back to store finder')}</span>
                    </BackButtonLabel>
                </Button>
            </span>

            {store.media?.images && store.media.images.length > 0 && <StoreImageCarousel images={store.media.images} />}

            <Text $as="h2" typography={DETAIL_HEADING_TYPOGRAPHY} color="var(--ui-primary-aa)" mb="10px" mt="40px">
                {store.name}
            </Text>
            <DetailContent>
                <ContactSection>
                    <SectionLabel>
                        <Text $as="span" typography={DETAIL_LABEL_TYPOGRAPHY} color="var(--ui-primary-aa)">
                            {t('store_finder.get_in_touch', 'Get in touch')}
                        </Text>
                    </SectionLabel>
                    <SectionContent>
                        <Text $as="span" typography={DETAIL_VALUE_TYPOGRAPHY} color="var(--ui-primary-aa)">
                            {store.address.streetAddress}{' '}
                            {store.address.city
                                ? `${store.address.city}, ${store.address.country}`
                                : store.address.country}
                        </Text>
                        <LinkList>
                            {store.contactInformation?.phoneNumber && (
                                <Button
                                    typography={DETAIL_LABEL_TYPOGRAPHY}
                                    theme="textButtonUnderline"
                                    url={`tel:${store.contactInformation.phoneNumber}`}
                                >
                                    {store.contactInformation.phoneNumber}
                                </Button>
                            )}
                            {store.contactInformation?.emailAddress && (
                                <Button
                                    typography={DETAIL_LABEL_TYPOGRAPHY}
                                    theme="textButtonUnderline"
                                    url={`mailto:${store.contactInformation.emailAddress}`}
                                >
                                    {store.contactInformation.emailAddress}
                                </Button>
                            )}
                            {store.contactInformation?.websiteUrl && (
                                <Button
                                    typography={DETAIL_LABEL_TYPOGRAPHY}
                                    theme="textButtonUnderline"
                                    url={store.contactInformation.websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {store.contactInformation.websiteUrl}
                                </Button>
                            )}
                        </LinkList>
                    </SectionContent>
                </ContactSection>

                {store.hours?.workingDays && (
                    <ContactSection>
                        <SectionLabel>
                            <Text $as="span" typography={DETAIL_LABEL_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                {t('store_finder.opening_hours', 'Opening hours')}
                            </Text>
                        </SectionLabel>
                        <SectionContent>
                            <OpeningHoursItem>
                                <Text $as="span" typography={DETAIL_SECONDARY_TYPOGRAPHY} color="var(--ui-primary-aa)">
                                    {store.hours.workingDays}
                                </Text>
                            </OpeningHoursItem>
                        </SectionContent>
                    </ContactSection>
                )}

                {store.content && store.content.length > 0 && (
                    <ContactSection>
                        <PortableText value={store.content} typography={DETAIL_SECONDARY_TYPOGRAPHY} />
                    </ContactSection>
                )}
            </DetailContent>
        </>
    );

    return (
        <OverlayWrapper
            $isOpen={isOpen}
            aria-hidden={!isOpen}
            role="dialog"
            aria-modal="true"
            aria-label={t('store_finder.title')}
            data-theme="light"
        >
            <PanelHeader data-theme="orange">
                <Text $as="span" typography={TITLE_TYPOGRAPHY} color="var(--ui-primary-aa)">
                    {t('store_finder.title')}
                </Text>
                <Button
                    theme="textButtonUnderline"
                    typography={TITLE_TYPOGRAPHY}
                    type="button"
                    onClick={onClose}
                    color="var(--ui-primary-aa)"
                >
                    {t('header.close', 'Close')}
                </Button>
            </PanelHeader>

            <ContentLayout>
                <MapContainer>
                    <StoreFinderMap
                        stores={filteredStores}
                        isOpen={isOpen}
                        selectedStore={selectedStore}
                        onMarkerClick={handleSelectStore}
                    />
                </MapContainer>

                <StorePanel>
                    <SlidingTrack ref={slidingTrackRef} $showDetail={!!selectedStore}>
                        <SlideView>{renderStoreList()}</SlideView>
                        <SlideView>{displayedStore && renderStoreDetail(displayedStore)}</SlideView>
                    </SlidingTrack>
                </StorePanel>
            </ContentLayout>
        </OverlayWrapper>
    );
};
